import { GiProcessor } from "react-icons/gi";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

import './contact.scss'

type From = { name: string, mail: string, text: string }

type ContactProps = {
  contactForm: From
  setContactForm: (value: From) => void
}

function Contact(props: Readonly<ContactProps>) {
  const { t, i18n } = useTranslation()
  const [errors, setErrors] = useState({ name: '', mail: '', text: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  useEffect(() => {
    document.title = 'Contact | Dwain'
  }, [])

  const handleForm = (type: string, value: string) => {
    props.setContactForm({...props.contactForm, [type]: value})
    if (errors[type as keyof typeof errors]) {
      setErrors({...errors, [type]: ''})
    }
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle')
    }
  }

  const checkValid = () => {
    const checkNameMail = props.contactForm.name.length === 0 || props.contactForm.text.length === 0
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const checkMail = regex.test(props.contactForm.mail)
    return !checkMail || checkNameMail
  }

  const handleButtonClick = async () => {
    if (checkValid()) {
      const newErrors = { name: '', mail: '', text: '' }

      if (props.contactForm.name.length === 0) {
        newErrors.name = t('contact.form.errorName')
      }

      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(props.contactForm.mail)) {
        newErrors.mail = t('contact.form.errorMail')
      }

      if (props.contactForm.text.length === 0) {
        newErrors.text = t('contact.form.errorText')
      }

      setErrors(newErrors)
    } else {
      setIsSubmitting(true)
      setSubmitStatus('idle')

      try {
        await fetch('https://script.google.com/macros/s/AKfycbzNv9NwdwsZTS8VM7cHsxgkEzHulcfYmkqZCI9Zybve_nKz-l_HuHJA1PfbYwyZpJFV/exec', {
          method: 'POST',
          mode: 'no-cors',
          body: JSON.stringify(props.contactForm)
        })

        setSubmitStatus('success')
        props.setContactForm({ name: '', mail: '', text: '' })
        setErrors({ name: '', mail: '', text: '' })
      } catch (error) {
        console.error('Error submitting form:', error)
        setSubmitStatus('error')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <main className='page-contact'>
      <div className='contact-content'>
        <header className='content-header'>
          <h1 className='header-title' >
            <span>{t('contact.title')}</span>
            <span className={`title-mid ${i18n.language === 'de' ? 'title-midColor' : ''}`}>{t('contact.title1')}</span>
            <span className={`title-mid ${i18n.language === 'en' ? 'title-midColor' : ''}`}>{t('contact.title2')}</span>
          </h1>
          <p className='header-subTitle' >{t('contact.subtitle')}</p>
        </header>
       <section className='content-card'>
        <div className='card-bg' role="presentation" aria-hidden="true"></div>
        <div className='card-bg card-bg2' role="presentation" aria-hidden="true"></div>
        <div className='card-content'>
          {(submitStatus === 'success' || submitStatus === 'error') && (
            <div className="form-overlay" role="alert">
              <div className={submitStatus === 'success' ? 'overlay-message success' : 'overlay-message error'}>
                {submitStatus === 'success' ? t('contact.form.successMessage') : t('contact.form.errorMessage')}
              </div>
            </div>
          )}
          <div className='content-container left' aria-hidden="true">
            <GiProcessor />
          </div>
          <div className='content-line' role="presentation" aria-hidden="true"/>
          <div className='content-container'>
            <form className="container-form" onSubmit={(e) => { e.preventDefault(); handleButtonClick(); }}>
              <div className="form-field">
                <label htmlFor="name" className="sr-only">Name</label>
                <input
                  id="name"
                  type="text"
                  onChange={(e) => {handleForm('name', e.target.value)}}
                  value={props.contactForm.name}
                  placeholder={t('contact.form.inputNameplaceholder')}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && <div id="name-error" className="error-message" role="alert">{errors.name}</div>}
              </div>
              <div className="form-field">
                <label htmlFor="email" className="sr-only">E-Mail</label>
                <input
                  id="email"
                  type="email"
                  onChange={(e) => {handleForm('mail', e.target.value)}}
                  value={props.contactForm.mail}
                  placeholder={t('contact.form.inputMailplaceholder')}
                  aria-invalid={!!errors.mail}
                  aria-describedby={errors.mail ? "email-error" : undefined}
                />
                {errors.mail && <div id="email-error" className="error-message" role="alert">{errors.mail}</div>}
              </div>
              <div className="form-field">
                <label htmlFor="message" className="sr-only">Message</label>
                <textarea
                  id="message"
                  onChange={(e) => {handleForm('text', e.target.value)}}
                  value={props.contactForm.text}
                  rows={14}
                  placeholder={t('contact.form.inputTextplaceholder')}
                  aria-invalid={!!errors.text}
                  aria-describedby={errors.text ? "message-error" : undefined}
                />
                {errors.text && <div id="message-error" className="error-message" role="alert">{errors.text}</div>}
              </div>
              {submitStatus === 'idle' && (
                <button
                  type="submit"
                  className={checkValid() || isSubmitting ? 'disabled' : ''}
                  disabled={isSubmitting}
                  aria-label={isSubmitting ? t('contact.form.sending') : t('contact.form.enterButton')}
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.enterButton')}
                </button>
              )}
            </form>
          </div>
        </div>
       </section>
      </div>
    </main>
  )
}

export default Contact
